import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../App';
import { Home, LogOut, Target, Calendar, CheckCircle, Circle } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_736b5617-324a-4027-a0b5-949393e5cf90/artifacts/te4le6nd_Untitled%20design.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

const frameworkStages = ['Foundation', 'Validation', 'Credibility', 'Brand', 'Systems', 'Protection', 'Launch'];

export default function ClientGoals() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [data, setData] = useState({ goals: [], tasks: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/portal/goals`, { withCredentials: true })
      .then(res => setData(res.data))
      .catch(err => { if (err.response?.status === 403) navigate('/'); })
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const getGoalTasks = (goalId) => data.tasks.filter(t => t.goal_id === goalId);

  if (loading) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="client-goals-page">
      <header className="bg-[#0F172A] text-white py-4">
        <div className="container-custom flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="DCA" className="h-10 w-auto" />
            <span className="text-lg font-bold">Client Portal</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#94A3B8]">{user?.name}</span>
            <Link to="/" className="text-[#94A3B8] hover:text-white"><Home size={20} /></Link>
            <button onClick={handleLogout} className="text-[#94A3B8] hover:text-white"><LogOut size={20} /></button>
          </div>
        </div>
      </header>

      <nav className="bg-white border-b border-[#E2E8F0]">
        <div className="container-custom flex items-center gap-6 py-4 overflow-x-auto">
          <Link to="/portal" className="text-sm font-medium text-[#475569] hover:text-[#1D4ED8] whitespace-nowrap">Dashboard</Link>
          <Link to="/portal/tasks" className="text-sm font-medium text-[#475569] hover:text-[#1D4ED8] whitespace-nowrap">My Tasks</Link>
          <Link to="/portal/goals" className="text-sm font-bold text-[#1D4ED8] whitespace-nowrap">My Plan</Link>
          <Link to="/portal/resources" className="text-sm font-medium text-[#475569] hover:text-[#1D4ED8] whitespace-nowrap">Resources</Link>
          <Link to="/portal/sessions" className="text-sm font-medium text-[#475569] hover:text-[#1D4ED8] whitespace-nowrap">Sessions</Link>
          <Link to="/portal/settings" className="text-sm font-medium text-[#475569] hover:text-[#1D4ED8] whitespace-nowrap">Settings</Link>
        </div>
      </nav>

      <main className="container-custom py-8">
        <h1 className="text-2xl font-bold text-[#0A0F1C] mb-2" data-testid="goals-title">My Business Plan</h1>
        <p className="text-[#475569] mb-8">Your goals aligned with the 7-Stage Business Jumpstart Framework</p>

        {/* Framework Progress */}
        <div className="bg-white p-6 border border-[#E2E8F0] rounded-lg mb-8">
          <h2 className="font-bold text-[#0A0F1C] mb-4">Framework Progress</h2>
          <div className="flex flex-wrap gap-3">
            {frameworkStages.map(stage => {
              const stageGoals = data.goals.filter(g => g.framework_stage === stage);
              const hasGoals = stageGoals.length > 0;
              const allComplete = hasGoals && stageGoals.every(g => g.status === 'completed');
              return (
                <div key={stage} className={`px-4 py-2 rounded-lg text-sm font-medium border ${allComplete ? 'bg-green-100 text-green-700 border-green-200' : hasGoals ? 'bg-[#EFF6FF] text-[#1D4ED8] border-[#1D4ED8]/20' : 'bg-[#F1F5F9] text-[#94A3B8] border-[#E2E8F0]'}`} data-testid={`stage-${stage.toLowerCase()}`}>
                  {allComplete && <CheckCircle size={14} className="inline mr-1" />}
                  {stage}
                </div>
              );
            })}
          </div>
        </div>

        {/* Goals */}
        {data.goals.length > 0 ? (
          <div className="space-y-6">
            {data.goals.map((goal, i) => {
              const goalTasks = getGoalTasks(goal.goal_id);
              const completedCount = goalTasks.filter(t => t.status === 'completed').length;
              return (
                <div key={goal.goal_id} className="bg-white p-6 border border-[#E2E8F0] rounded-lg" data-testid={`goal-card-${i}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-[#0A0F1C] text-lg">{goal.title}</h3>
                      {goal.description && <p className="text-sm text-[#475569] mt-1">{goal.description}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      {goal.framework_stage && <span className="text-xs px-2 py-1 rounded bg-[#EFF6FF] text-[#1D4ED8]">{goal.framework_stage}</span>}
                      <span className={`text-xs px-2 py-1 rounded ${goal.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-[#F1F5F9] text-[#475569]'}`}>{goal.status}</span>
                    </div>
                  </div>
                  {goal.target_date && <p className="text-xs text-[#94A3B8] flex items-center gap-1 mb-3"><Calendar size={12} />Target: {goal.target_date}</p>}
                  {goalTasks.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[#F1F5F9]">
                      <span className="text-xs text-[#94A3B8] mb-2 block">Tasks: {completedCount}/{goalTasks.length} completed</span>
                      <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                        <div className="h-full bg-[#1D4ED8]" style={{ width: `${goalTasks.length ? (completedCount / goalTasks.length * 100) : 0}%` }}></div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white p-8 border border-[#E2E8F0] rounded-lg text-center">
            <Target size={48} className="text-[#E2E8F0] mx-auto mb-4" />
            <p className="text-[#475569]">No goals set yet. Your consultant will create your business plan soon.</p>
          </div>
        )}
      </main>
    </div>
  );
}
